import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useParams, Link, Redirect } from "react-router-dom";
import {
  FaBookOpen,
  FaClipboardList,
  FaCog,
  FaCopy,
  FaEdit,
  FaPlus,
  FaSyncAlt,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import MdlEditPaper from "../dialog/MdlEditPaper";
import MdlDeletePaper from "../dialog/MdlDeletePaper";
import MdlAddNewPaper from "../dialog/MdlAddNewPaper";
import MdlAddMultiplePapers from "../dialog/MdlAddMultiplePapers";
import MdlSelectClass from "../dialog/MdlSelectClass";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (item.section_id === "No" || !item.section_name) {
    return item.class_name || "Class";
  }
  return `${item.class_name}: ${item.section_name}`;
}

//Created By Cursor
function papersForSubject(classItem, subjectId) {
  const papers = classItem?.subject_paper || [];
  return papers.filter((paper) => paper.subject_id === subjectId);
}

const ExamPaperSettings = () => {
  const { exam_id, session_id, exam_name, exam_status } = useParams();
  const loggedIn = localStorage.getItem("token") !== null;

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [EXAM_ID] = useState(exam_id || "");
  const [SESSION_ID] = useState(session_id || "");
  const [EXAM_NAME] = useState(decodeURIComponent(exam_name || ""));
  const [EXAM_STATUS] = useState(decodeURIComponent(exam_status || ""));

  const [CLASS_ID, setClassId] = useState("");
  const [SECTION_ID, setSectionId] = useState("");
  const [class_section_name, setClassSectionName] = useState("");
  const [mClassList, setClassList] = useState([]);
  const [CLASS_SELECTED_POSITION, setClassSelectedPosition] = useState(0);
  const [SUJECT_SELECTED_POSITION, setSubjectSelectedPosition] = useState(0);
  const [mSubject, setSubject] = useState([]);
  const [SelectedSubjectid, setSelectedSubjectid] = useState();
  const [SelectedSubjectName, setSelectedSubjectName] = useState();
  const [mSelectPaper, setSelectPaper] = useState();

  const [isOpenEditPaper, setIsOpenEditPaper] = useState(false);
  const [isOpenDeletePaper, setIsOpenDeletePaper] = useState(false);
  const [isOpenAddNewPaper, setIsOpenAddNewPaper] = useState(false);
  const [isOpenSelectClass, setIsOpenSelectClass] = useState(false);
  const [isOpenAddMultiplePapers, setIsOpenAddMultiplePapers] = useState(false);

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setClassList([]);
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE()}getClassWithSubjectsPartAndPaper.php`,
          {
            params: {
              school_code: schoolCode,
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          setClassList(response.data.data || []);
        } else {
          setClassList([]);
          myToast.notify_warning(
            response.data?.api_remark || "Could not load paper settings."
          );
        }
      } catch (error) {
        console.error("Error fetching paper settings:", error);
        setClassList([]);
        myToast.notify_warning("Network error loading paper settings.");
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  //Created By Cursor
  const handleSelectClass = useCallback(
    (selectedPosition) => {
      if (!mClassList.length) {
        setClassId("");
        setSectionId("");
        setClassSectionName("");
        setSubject([]);
        setSubjectSelectedPosition(0);
        return;
      }

      const selected = mClassList[selectedPosition];
      setClassId(selected?.class_id || "");
      setSectionId(selected?.section_id || "No");
      setClassSectionName(classLabel(selected));
      setSubject(selected?.subject || []);
      setSubjectSelectedPosition(0);
    },
    [mClassList]
  );

  useEffect(() => {
    if (CLASS_SELECTED_POSITION >= 0) {
      handleSelectClass(CLASS_SELECTED_POSITION);
    }
  }, [CLASS_SELECTED_POSITION, handleSelectClass]);

  useEffect(() => {
    const selectedSubjects = mSubject[SUJECT_SELECTED_POSITION];
    if (selectedSubjects) {
      setSelectedSubjectid(selectedSubjects.subject_id);
      setSelectedSubjectName(selectedSubjects.subject_name);
    }
  }, [SUJECT_SELECTED_POSITION, mSubject]);

  const selectedClass = mClassList[CLASS_SELECTED_POSITION] || null;

  const maxPapers = useMemo(() => {
    if (!mSubject.length) return 1;
    const counts = mSubject.map(
      (subject) => papersForSubject(selectedClass, subject.subject_id).length
    );
    return Math.max(1, ...counts);
  }, [mSubject, selectedClass]);

  const hasPapersInOtherClasses = useMemo(
    () =>
      mClassList.some((classItem, index) => {
        if (index === CLASS_SELECTED_POSITION) return false;
        return (classItem.subject_paper || []).length > 0;
      }),
    [mClassList, CLASS_SELECTED_POSITION]
  );

  const kpi = useMemo(() => {
    const paperCount = (selectedClass?.subject_paper || []).length;
    return {
      classCount: mClassList.length,
      subjectCount: mSubject.length,
      paperCount,
    };
  }, [mClassList.length, mSubject.length, selectedClass]);

  //Created By Cursor
  const openAddPaper = useCallback((subject, subjectIndex) => {
    setSubjectSelectedPosition(subjectIndex);
    setSelectedSubjectid(subject.subject_id);
    setSelectedSubjectName(subject.subject_name);
    setIsOpenAddNewPaper(true);
  }, []);

  //Created By Cursor
  const toggleEditPaper = useCallback((paper) => {
    setSelectPaper(paper);
    setIsOpenEditPaper(true);
  }, []);

  //Created By Cursor
  const toggleDeletePaper = useCallback((paper) => {
    setSelectPaper(paper);
    setIsOpenDeletePaper(true);
  }, []);

  //Created By Cursor
  const refreshSoft = useCallback(() => {
    getExamParticipatingClass({ soft: true });
  }, [getExamParticipatingClass]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  const detailsPath = `/AdminMain/ExamDetails/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(
    EXAM_NAME || ""
  )}/${encodeURIComponent(EXAM_STATUS || "")}`;

  return (
    <div
      className="content payroll-page sm-page eps-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Paper Settings</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCog />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Paper Settings</h1>
              <p className="sm-hero__sub">
                {EXAM_NAME || "Exam"} · Configure subject papers & max marks
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {EXAM_STATUS ? (
              <span className="eps-status-pill" title="Exam status">
                {EXAM_STATUS}
              </span>
            ) : null}
            <button
              type="button"
              onClick={refreshSoft}
              disabled={refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to={detailsPath} className="eps-hero-link">
              Exam Details
            </Link>
            <Link to="/AdminMain/ExamList" className="eps-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="eps-content">
          <div className="eps-kpi-row">
            <span className="eps-kpi">
              <FaUsers aria-hidden />
              {kpi.classCount} classes
            </span>
            <span className="eps-kpi">
              <FaBookOpen aria-hidden />
              {kpi.subjectCount} subjects
            </span>
            <span className="eps-kpi">
              <FaClipboardList aria-hidden />
              {kpi.paperCount} papers
            </span>
          </div>

          {/* Participating classes */}
          <section className="eps-section-card" aria-label="Participating classes">
            <div className="eps-section-card__header">
              <h2 className="eps-section-card__title">Participating Classes</h2>
              <span className="eps-section-card__count">{mClassList.length}</span>
            </div>
            <div className="eps-section-card__body">
              {mClassList.length === 0 ? (
                <div className="eps-empty">
                  <p className="eps-empty__title">No participating classes</p>
                  <p className="eps-empty__hint">
                    Add classes from Exam Details first.
                  </p>
                </div>
              ) : (
                <div className="eps-class-grid">
                  {mClassList.map((classItem, index) => (
                    <button
                      key={`${classItem.class_id}-${classItem.section_id}-${index}`}
                      type="button"
                      className={`eps-class-chip${
                        CLASS_SELECTED_POSITION === index ? " is-active" : ""
                      }`}
                      onClick={() => setClassSelectedPosition(index)}
                    >
                      {classLabel(classItem)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Subject-paper matrix */}
          <section className="eps-section-card" aria-label="Subject papers matrix">
            <div className="eps-section-card__header">
              <h2 className="eps-section-card__title">
                Subject Papers · {classLabel(selectedClass)}
              </h2>
              <div className="eps-header-actions">
                <button
                  type="button"
                  className="eps-header-btn"
                  disabled={!hasPapersInOtherClasses}
                  onClick={() => setIsOpenSelectClass(true)}
                  title={
                    hasPapersInOtherClasses
                      ? "Copy papers from another class"
                      : "No papers in other classes"
                  }
                >
                  <FaCopy aria-hidden /> Copy From Class
                </button>
                <button
                  type="button"
                  className="eps-header-btn eps-header-btn--solid"
                  onClick={() => setIsOpenAddMultiplePapers(true)}
                  disabled={!mSubject.length}
                >
                  <FaPlus aria-hidden /> Create New Paper
                </button>
              </div>
            </div>
            <div className="eps-section-card__body">
              {!mSubject.length ? (
                <div className="eps-empty">
                  <p className="eps-empty__title">No subjects found</p>
                  <p className="eps-empty__hint">
                    No subjects are available for the selected class.
                  </p>
                </div>
              ) : (
                <div className="eps-table-wrap">
                  <table className="eps-matrix">
                    <thead>
                      <tr>
                        <th rowSpan={2} className="eps-matrix__subject-head">
                          Subject
                        </th>
                        {Array.from({ length: maxPapers }, (_, paperIndex) => (
                          <th key={paperIndex} colSpan={3}>
                            Paper {paperIndex + 1}
                          </th>
                        ))}
                        <th rowSpan={2} className="eps-matrix__action-head">
                          Add More
                        </th>
                      </tr>
                      <tr>
                        {Array.from({ length: maxPapers }, (_, paperIndex) => (
                          <React.Fragment key={`sub-${paperIndex}`}>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Max</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mSubject.map((subject, subjectIndex) => {
                        const subjectPapers = papersForSubject(
                          selectedClass,
                          subject.subject_id
                        );

                        return (
                          <tr key={`${subject.subject_id}-${subjectIndex}`}>
                            <td className="eps-matrix__subject">
                              <span className="eps-matrix__subject-name">
                                {subject.subject_name}
                              </span>
                              {subject.subject_type ? (
                                <span className="eps-matrix__subject-type">
                                  {subject.subject_type}
                                </span>
                              ) : null}
                            </td>

                            {Array.from({ length: maxPapers }, (_, paperIndex) => {
                              const paper = subjectPapers[paperIndex];
                              return (
                                <React.Fragment
                                  key={`${subject.subject_id}-p-${paperIndex}`}
                                >
                                  <td
                                    className={`eps-matrix__cell${
                                      paper ? " has-paper" : ""
                                    }`}
                                  >
                                    {paper ? (
                                      <div className="eps-paper-cell">
                                        <div className="eps-paper-cell__name">
                                          {paper.exam_paper_name}
                                        </div>
                                        <div className="eps-paper-cell__actions">
                                          <button
                                            type="button"
                                            className="eps-mini-btn"
                                            onClick={() => toggleEditPaper(paper)}
                                            title="Edit paper"
                                          >
                                            <FaEdit aria-hidden /> Edit
                                          </button>
                                          <button
                                            type="button"
                                            className="eps-mini-btn eps-mini-btn--danger"
                                            onClick={() =>
                                              toggleDeletePaper(paper)
                                            }
                                            title="Delete paper"
                                          >
                                            <FaTrash aria-hidden /> Delete
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="eps-add-btn"
                                        onClick={() =>
                                          openAddPaper(subject, subjectIndex)
                                        }
                                      >
                                        <FaPlus aria-hidden /> Add Paper
                                      </button>
                                    )}
                                  </td>
                                  <td
                                    className={`eps-matrix__cell eps-matrix__cell--center${
                                      paper ? " has-paper" : ""
                                    }`}
                                  >
                                    {paper ? paper.paper_type || "—" : "—"}
                                  </td>
                                  <td
                                    className={`eps-matrix__cell eps-matrix__cell--center${
                                      paper ? " has-paper" : ""
                                    }`}
                                  >
                                    {paper ? (
                                      <div className="eps-max">
                                        <span
                                          className={`eps-max__badge${
                                            paper.is_mark_system === "1"
                                              ? " is-marks"
                                              : " is-grades"
                                          }`}
                                        >
                                          {paper.is_mark_system === "1"
                                            ? "Marks"
                                            : "Grades"}
                                        </span>
                                        <strong>
                                          {paper.is_mark_system === "1"
                                            ? paper.max_mark
                                            : paper.max_grade}
                                        </strong>
                                      </div>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </React.Fragment>
                              );
                            })}

                            <td className="eps-matrix__cell eps-matrix__cell--center">
                              <button
                                type="button"
                                className="eps-add-btn eps-add-btn--solid"
                                onClick={() =>
                                  openAddPaper(subject, subjectIndex)
                                }
                              >
                                <FaPlus aria-hidden /> Add Paper
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {isOpenAddNewPaper ? (
        <MdlAddNewPaper
          actionClose={() => setIsOpenAddNewPaper(false)}
          actionAdd={() => {
            setIsOpenAddNewPaper(false);
            refreshSoft();
          }}
          exam_id={EXAM_ID}
          exam_name={EXAM_NAME}
          session_id={SESSION_ID}
          section_id={SECTION_ID}
          class_id={CLASS_ID}
          subject_id={SelectedSubjectid}
          subject_name={SelectedSubjectName}
          class_section_name={class_section_name}
        />
      ) : null}

      {isOpenEditPaper ? (
        <MdlEditPaper
          actionClose={() => setIsOpenEditPaper(false)}
          actionUpdate={() => {
            setIsOpenEditPaper(false);
            refreshSoft();
          }}
          paper={mSelectPaper}
          exam_id={EXAM_ID}
          exam_name={EXAM_NAME}
        />
      ) : null}

      {isOpenDeletePaper ? (
        <MdlDeletePaper
          actionClose={() => setIsOpenDeletePaper(false)}
          actionDelete={() => {
            setIsOpenDeletePaper(false);
            refreshSoft();
          }}
          paper={mSelectPaper}
          exam_id={EXAM_ID}
          exam_name={EXAM_NAME}
        />
      ) : null}

      {isOpenSelectClass ? (
        <MdlSelectClass
          isOpen={isOpenSelectClass}
          onClose={() => setIsOpenSelectClass(false)}
          onSelect={() => {
            setIsOpenSelectClass(false);
            refreshSoft();
          }}
          exam_id={EXAM_ID}
          session_id={SESSION_ID}
          current_class_id={CLASS_ID}
          current_section_id={SECTION_ID}
        />
      ) : null}

      {isOpenAddMultiplePapers ? (
        <MdlAddMultiplePapers
          isOpen={isOpenAddMultiplePapers}
          onClose={() => setIsOpenAddMultiplePapers(false)}
          onSuccess={() => {
            setIsOpenAddMultiplePapers(false);
            refreshSoft();
          }}
          exam_id={EXAM_ID}
          session_id={SESSION_ID}
          class_id={CLASS_ID}
          section_id={SECTION_ID}
          availableSubjects={mSubject || []}
        />
      ) : null}
    </div>
  );
};

export default ExamPaperSettings;
